import { AxiosResponse } from "axios";
import {
  axiosAuthenticated,
  httpClient,
  uploadImageAndGetCdnUrl,
} from "../../../service/http";
import {
  SalesChannelItem,
  SalesChannelListItem,
  SalesChannelSuccessResponse,
  SalesChannelsSuccessResponse,
} from "../models/sales-channel";
import { OrganizationKeyName } from "../../../model/Organization";
import { LinkProductToShopType } from "../../../service/api-response/LinkProductToShop";
import { UnlinkProductToShopType } from "../../../service/api-response/UnlinkProductFromShop";
import {
  CreateShop,
  Shop,
  ShopSuccessResponse,
  UpdateShop,
} from "../model/ShopSuccessResponse";
import { ShopListSuccessResponse } from "../model/ShopListSuccessResponse";
import { PostPayload, UpdateData } from "../../../model/http-crud-models";
import {
  TimeslotTemaplatesSuccessResponse,
  TimeslotTemplateCreate,
  TimeslotTemplateUpdate,
} from "../model/TimeslotTemplatesSuccessResponse";
import { UploadImage } from "../../../service/api-response/UploadImage";

export const shopHttp = {
  getSalesChannels: async (
    organizationKeyName: string,
    shopId?: string
  ): Promise<SalesChannelListItem[]> => {
    const url = `/organizations/${organizationKeyName}/sales-channels/items?shop=${shopId}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<SalesChannelsSuccessResponse>
    >(url);
    const salesChannels = response.data.data;
    return salesChannels;
  },

  getSalesChannel: async (
    organizationKeyName: string,
    id?: string
  ): Promise<SalesChannelItem> => {
    const url = `/organizations/${organizationKeyName}/sales-channels/items/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<SalesChannelSuccessResponse>
    >(url);
    const salesChannel = response.data.data;
    return salesChannel;
  },

  linkProductToShop: async ({
    organizationKeyName,
    productId,
    shopId,
  }: LinkProductToShopType) => {
    const response = await axiosAuthenticated.put(
      `/organizations/${organizationKeyName}/shop-link-product`,
      { shopId, productId }
    );

    return response.data.data;
  },

  unlinkProductToShop: async ({
    organizationKeyName,
    productId,
    shopId,
  }: UnlinkProductToShopType) => {
    const response = await axiosAuthenticated.put(
      `/organizations/${organizationKeyName}/shop-unlink-product`,
      { shopId, productId }
    );

    return response.data.data;
  },

  getShops: async (organizationKeyName?: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/shops/items`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<ShopListSuccessResponse>
    >(url);
    return response.data.data;
  },

  getShop: async (organizationKeyName: string, id?: string): Promise<Shop> => {
    const url = `/organizations/${organizationKeyName}/shops/items/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<ShopSuccessResponse>
    >(url);
    const shop = response.data.data;
    return shop;
  },

  updateShop: async ({ organizationKeyName, id, data }: UpdateShop) => {
    const url = `/organizations/${organizationKeyName}/shops/items/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  createShop: async ({ organizationKeyName, data }: CreateShop) => {
    const url = `/organizations/${organizationKeyName}/shops/items`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<{ id: string }>
    >(url, data);

    return response.data.id;
  },

  listTimeslots: async (organizationKeyName: string, shopId: string) => {
    const url = `/organizations/${organizationKeyName}/shops/${shopId}/timeslot-templates`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<TimeslotTemaplatesSuccessResponse>
    >(url);
    const timeslots = response.data.timeslots;
    return timeslots;
  },

  copyAndSetTimeslotsFromShop: async ({
    organizationKeyName,
    data,
  }: PostPayload<{ sourceShopId: string; targetShopId: string }>) => {
    return httpClient<{}>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shops/timeslot-templates/overwrite`,
      data,
    });
  },

  createTimeslotTemplate: async ({
    organizationKeyName,
    data,
  }: PostPayload<TimeslotTemplateCreate & { shopId: string }>) => {
    const { shopId, ...rest } = data;
    return httpClient<{}>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shops/${shopId}/timeslot-templates/create`,
      data: rest,
    });
  },

  updateTimeslot: async ({
    organizationKeyName,
    id,
    data,
  }: UpdateData<TimeslotTemplateUpdate>) => {
    return httpClient<{}>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shops/${id}/timeslot-templates/update`,
      data: {
        templateRequests: [
          {
            templateId: data.id,
            ...data,
          },
        ],
      },
    });
  },

  uploadAllergensPdf: async ({
    file,
    id,
    organizationKeyName,
  }: UploadImage) => {
    return uploadImageAndGetCdnUrl({
      file,
      id,
      organizationKeyName,
      apiRoute: "shop-allergens-pdf",
    });
  },
};
